<script setup lang="ts">
import { useMediaQuery, useStorage } from "@vueuse/core"
import { computed, ref } from "vue"
import { Alert, Button, Icon } from "@/components/ui"
import { usePwaInstall, useTranslation } from "@/composables"
import InstallIosModal from "./InstallIosModal.vue"

interface InstallBannerProps {
  withContainer?: boolean
}

withDefaults(defineProps<InstallBannerProps>(), {
  withContainer: false,
})

const { t } = useTranslation()

const { isInstallPromptSupported, isIos, isInstalled, isLoading, promptInstall } = usePwaInstall()
const isIosModalOpen = ref(false)

const isDismissed = useStorage("install-banner-dismissed", false)
const isAnimating = ref(false)

const isMdUp = useMediaQuery("(min-width: 768px)")

const openIosModal = () => isIosModalOpen.value = true

function dismissBanner() {
  isAnimating.value = true
  setTimeout(() => {
    isDismissed.value = true
    isAnimating.value = false
  }, 300)
}

function handleInstall() {
  if (isInstallPromptSupported.value)
    promptInstall()
  else
    openIosModal()
}

const bannerClasses = computed(() => ({
  "opacity-100": !isAnimating.value,
  "opacity-0": isAnimating.value,
  "transition-opacity duration-300": true,
}))

const displayBanner = computed(() => !isLoading.value && !isInstalled.value && !isDismissed.value && (isInstallPromptSupported.value || isIos.value))
</script>

<template>
  <Alert
    v-if="displayBanner"
    compact
    variant="primary-solid"
    sharp-corners
    :closable="false"
    :class="bannerClasses"
  >
    <div :class="{ 'container mx-auto': withContainer }">
      <div class="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
        <div class="flex min-w-0 flex-1 items-center gap-3">
          <span
            class="flex size-10 shrink-0 items-center justify-center rounded-full bg-primary-card-text/15 text-primary-card-text"
            aria-hidden="true"
          >
            <Icon icon="Download" class="text-primary-card-text fill-primary-card-text" size="sm" />
          </span>
          <div class="min-w-0 flex-1 pt-0.5">
            <h3 class="font-heading text-sm font-bold leading-tight sm:text-base">
              {{ t("install_banner.title") }}
            </h3>
            <p class="mt-0.5 font-body text-xs hidden md:block leading-snug sm:text-sm">
              {{ t("install_banner.message") }}
            </p>
          </div>
          <div class="flex items-center gap-2">
            <Button
              variant="tertiary"
              :size="isMdUp ? 'default' : 'sm'"
              data-test="install-btn"
              @click="handleInstall"
            >
              {{ t("install_banner.install_btn") }}
            </Button>
            <button
              type="button"
              class="shrink-0 -mr-1 p-2 text-white/70 transition-colors hover:text-white cursor-pointer"
              data-test="install-banner-dismiss"
              :aria-label="t('install_banner.dismiss_btn')"
              @click="dismissBanner"
            >
              <Icon icon="Close" size="sm" class=" text-primary-card-text fill-primary-card-text" />
            </button>
          </div>
        </div>
      </div>
    </div>
  </Alert>
  <InstallIosModal v-model:open="isIosModalOpen" />
</template>
